import api from './api';
import type { ApiResponse } from '@/types/api.types';
import type {
  BackupDestination,
  BackupDestinationsResponse,
  CreateOnPremiseDestinationRequest,
  UpdateDestinationRequest,
} from '@/types/backup-destination.types';

/**
 * Backup destinations (ADR-041, single destination model).
 *
 * Cloud destinations are read-only projections of the worker's environment
 * (the backend lists them from the worker's GET /api/v2/cloud-configs); the
 * console creates, edits, probes and deletes on-premise destinations only.
 * Connection tests are asynchronous (ADR-041 D3): POST /:id/test answers 202
 * with the row in `verifying`, and waitForConnectionTest polls GET /:id.
 */
export const backupDestinationService = {
  /**
   * Get all backup destinations (cloud from the worker + on-premise rows)
   */
  async getDestinations(): Promise<BackupDestinationsResponse> {
    const response = await api.get<ApiResponse<{ data: BackupDestination[]; pagination: { total: number } }>>('/api/v1/backup-destinations');
    const data = response.data.data;
    return {
      destinations: data?.data || [],
      total: data?.pagination?.total || 0,
    };
  },

  /**
   * Read one destination (used to poll a running connection test)
   */
  async getDestination(id: string): Promise<BackupDestination> {
    const response = await api.get<ApiResponse<BackupDestination>>(`/api/v1/backup-destinations/${id}`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to read destination');
  },

  /**
   * Create an on-premise backup destination
   */
  async createOnPremiseDestination(data: CreateOnPremiseDestinationRequest): Promise<BackupDestination> {
    const response = await api.post<ApiResponse<BackupDestination>>('/api/v1/backup-destinations/on-premise', data);
    return response.data.data!;
  },

  /**
   * Update an on-premise backup destination (cloud rows answer 400 CLOUD_READ_ONLY)
   */
  async updateDestination(id: string, data: UpdateDestinationRequest): Promise<BackupDestination> {
    const response = await api.put<ApiResponse<BackupDestination>>(`/api/v1/backup-destinations/${id}`, data);
    return response.data.data!;
  },

  /**
   * Delete a backup destination
   */
  async deleteDestination(id: string): Promise<void> {
    await api.delete(`/api/v1/backup-destinations/${id}`);
  },

  /**
   * Start a connection test (202). The returned row is in `verifying`;
   * follow up with waitForConnectionTest. 400 CLOUD_READ_ONLY for cloud
   * destinations, 409 TEST_IN_PROGRESS while one is still running.
   */
  async testConnection(id: string): Promise<BackupDestination> {
    const response = await api.post<ApiResponse<BackupDestination>>(
      `/api/v1/backup-destinations/${id}/test`,
      {},
      { skipGlobalError: true },
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to start connection test');
  },

  /**
   * Poll a destination until its connection test finishes (status leaves
   * `verifying`). Resolves with the final row; rejects on timeout.
   */
  async waitForConnectionTest(
    id: string,
    opts: { intervalMs?: number; timeoutMs?: number } = {},
  ): Promise<BackupDestination> {
    const intervalMs = opts.intervalMs ?? 2000;
    const deadline = Date.now() + (opts.timeoutMs ?? 120_000);
    for (;;) {
      const current = await this.getDestination(id);
      if (current.status !== 'verifying') return current;
      if (Date.now() >= deadline) throw new Error('connection test timed out');
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  },

  /**
   * Enable a backup destination. The backend answers {success, message}
   * with no row (re-audit axis A); callers re-list.
   */
  async enableDestination(id: string): Promise<void> {
    await api.post<ApiResponse>(`/api/v1/backup-destinations/${id}/enable`);
  },

  /**
   * Disable a backup destination (same shape as enable).
   */
  async disableDestination(id: string): Promise<void> {
    await api.post<ApiResponse>(`/api/v1/backup-destinations/${id}/disable`);
  },
};
